import * as React from 'react';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    Container,
    Avatar
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import PeopleIcon from '@mui/icons-material/People';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PersonIcon from '@mui/icons-material/Person';
import { MilitaryTechOutlined, SportsGolfOutlined } from '@mui/icons-material';
import SecurityTrimmer from './SecurityTrimmer';

const drawerWidth = 240;

export interface IAdminLayoutProps {
    /** Título de la entidad actual (Tournaments, MiniGames, etc.) */
    entityTitle: string;
    /** Icono de la entidad actual */
    entityIcon?: React.ReactNode;
    /** Toolbar específica de la entidad (botones Crear/Editar/Eliminar) */
    entityToolbar?: React.ReactNode;
    /** Contenido principal (grid / tabla) */
    children?: React.ReactNode;
}

export interface IAdminLayoutState {
    mobileOpen: boolean;
    userMenuAnchor: HTMLElement | null;
}

export default class AdminLayout extends React.Component<
    IAdminLayoutProps,
    IAdminLayoutState
> {
    public constructor(props: IAdminLayoutProps) {
        super(props);
        this.state = {
            mobileOpen: false,
            userMenuAnchor: null
        };

        this.handleDrawerToggle = this.handleDrawerToggle.bind(this);
        this.handleUserMenuOpen = this.handleUserMenuOpen.bind(this);
        this.handleUserMenuClose = this.handleUserMenuClose.bind(this);
        this.handleLogout = this.handleLogout.bind(this);
    }

    private handleDrawerToggle(): void {
        this.setState((prev) => ({ mobileOpen: !prev.mobileOpen }));
    }

    private handleUserMenuOpen(event: React.MouseEvent<HTMLElement>): void {
        this.setState({ userMenuAnchor: event.currentTarget });
    }

    private handleUserMenuClose(): void {
        this.setState({ userMenuAnchor: null });
    }

    private handleLogout(): void {
        // Limpiamos sesión básica
        localStorage.removeItem('vg_token');
        localStorage.removeItem('vg_user');
        this.handleUserMenuClose();
        window.location.href = '/login';
    }

    private navigate(path: string): void {
        // Más adelante lo podemos cambiar por React Router si quieres
        window.location.href = path;
    }

    private getCurrentUserName(): string {
        const userJson = localStorage.getItem('vg_user');
        if (!userJson) {
            return 'Usuario';
        }

        try {
            const user = JSON.parse(userJson);
            return user.name || user.email || 'Usuario';
        } catch {
            return 'Usuario';
        }
    }

    private getUserInitials(): string {
        try {
            const userJson = localStorage.getItem("vg_user");
            if (!userJson) return "?";

            const user = JSON.parse(userJson);

            const fullName: string = user.name || user.email || "";
            if (!fullName) return "?";

            const parts = fullName.trim().split(/\s+/);

            if (parts.length === 1) {
                return parts[0][0].toUpperCase();
            }

            const first = parts[0][0];
            const second = parts[1][0];

            return (first + second).toUpperCase();
        } catch {
            return "?";
        }
    }

    private renderDrawerContent(): React.ReactNode {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Toolbar>
                    <Typography variant="h6" noWrap>
                        Vivo Golf
                    </Typography>
                </Toolbar>
                <Divider />
                <List>
                    <ListItemButton onClick={() => this.navigate('/tournaments')} selected={window.location.href.endsWith('/tournaments')}>
                        <ListItemIcon>
                            <EmojiEventsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Torneos" />
                    </ListItemButton>

                    <ListItemButton onClick={() => this.navigate('/games')} selected={window.location.href.endsWith('/') || window.location.href.endsWith('/games')}>
                        <ListItemIcon>
                            <SportsGolfOutlined />
                        </ListItemIcon>
                        <ListItemText primary="Juegos" />
                    </ListItemButton>

                    <SecurityTrimmer role='SuperAdmin'>
                        <ListItemButton onClick={() => this.navigate('/prizes')} selected={window.location.href.endsWith('/prizes')}>
                            <ListItemIcon>
                                <CardGiftcardIcon />
                            </ListItemIcon>
                            <ListItemText primary="Premios" />
                        </ListItemButton>
                    </SecurityTrimmer>

                    <SecurityTrimmer role='SuperAdmin'>
                        <ListItemButton onClick={() => this.navigate('/users')} selected={window.location.href.endsWith('/users')}>
                            <ListItemIcon>
                                <PersonIcon />
                            </ListItemIcon>
                            <ListItemText primary="Usuarios" />
                        </ListItemButton>
                    </SecurityTrimmer>
                </List>
            </Box>
        );
    }

    public render(): React.ReactElement<IAdminLayoutProps> {
        const { entityTitle, entityIcon, entityToolbar, children } = this.props;
        const { mobileOpen, userMenuAnchor } = this.state;
        const userName = this.getCurrentUserName();

        const drawer = this.renderDrawerContent();

        const isUserMenuOpen = Boolean(userMenuAnchor);

        return (
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />

                {/* AppBar */}
                <AppBar
                    position="fixed"
                    sx={{
                        zIndex: (theme) => theme.zIndex.drawer + 1
                    }}
                >
                    <Toolbar>
                        {/* Botón menú */}
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={this.handleDrawerToggle}
                            sx={{ mr: 2, display: { sm: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Nombre de la aplicación */}
                        <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
                            Vivo Golf Juegos
                        </Typography>

                        {/* Nombre de usuario + menú */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Avatar */}
                            <Avatar
                                sx={{
                                    bgcolor: 'secondary.main',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                }}
                                onClick={this.handleUserMenuOpen}
                            >
                                {this.getUserInitials()}
                            </Avatar>

                            {/* Nombre del usuario */}
                            <Typography
                                variant="body1"
                                sx={{ cursor: 'pointer' }}
                                onClick={this.handleUserMenuOpen}
                            >
                                {userName}
                            </Typography>

                            <Menu
                                anchorEl={userMenuAnchor}
                                open={isUserMenuOpen}
                                onClose={this.handleUserMenuClose}
                            >
                                <MenuItem onClick={this.handleLogout}>Cerrar sesión</MenuItem>
                            </Menu>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* Drawer lateral */}
                <Box
                    component="nav"
                    sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                    aria-label="menú de administración"
                >
                    {/* Mobile */}
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={this.handleDrawerToggle}
                        ModalProps={{
                            keepMounted: true
                        }}
                        sx={{
                            display: { xs: 'block', sm: 'none' },
                            '& .MuiDrawer-paper': { width: drawerWidth }
                        }}
                    >
                        {drawer}
                    </Drawer>

                    {/* Desktop */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' }
                        }}
                        open
                    >
                        {drawer}
                    </Drawer>
                </Box>

                {/* Contenido principal */}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        width: { sm: `calc(100% - ${drawerWidth}px)` },
                        minHeight: '100vh',
                        bgcolor: 'background.default'
                    }}
                >
                    <Toolbar /> {/* separación por el AppBar */}

                    <Container maxWidth="lg" sx={{ py: 3 }}>
                        {/* 2. Sección de título de entidad */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 4,
                                pb: 2,
                                gap: 2,
                                borderBottom: 'solid 1px #333'
                            }}
                        >
                            {entityIcon && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>{entityIcon}</Box>
                            )}
                            <Typography variant="h3" fontWeight={300}>
                                {entityTitle}
                            </Typography>
                        </Box>

                        {/* 3. Barra de herramientas */}
                        {entityToolbar && (
                            <Box
                                sx={{
                                    mb: 2,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1
                                }}
                            >
                                {entityToolbar}
                            </Box>
                        )}

                        {/* 4. Contenido (grid / tabla) */}
                        {children}
                    </Container>
                </Box>
            </Box>
        );
    }
}
